import { useEffect, useRef, useState } from 'react';
import { milliseconds } from 'date-fns';

import { cn } from '@/lib/utils.ts';

interface TextRotatorProps {
  messages: string[];
  interval?: number;
  className?: string;
}

/**
 * A component that rotates through an array of text messages with a fade animation.
 * Useful for loading screens to keep users engaged during wait times.
 */
export function TextRotator({
  messages,
  interval = milliseconds({ seconds: 7 }),
  className,
}: TextRotatorProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const innerTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (messages.length <= 1) return;
    if (currentIndex >= messages.length - 1) return; // Stop at the last message

    const outerTimer = setTimeout(() => {
      // Step 1: Fade out
      setIsTransitioning(true);

      // Step 2: After fade out, update message and fade back in
      innerTimerRef.current = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        setIsTransitioning(false);
        innerTimerRef.current = null;
      }, 300); // Match the CSS transition duration
    }, interval);

    return () => {
      clearTimeout(outerTimer);
      if (innerTimerRef.current) {
        clearTimeout(innerTimerRef.current);
        innerTimerRef.current = null;
      }
    };
  }, [messages, interval, currentIndex]);

  return (
    <p
      className={cn(
        'text-center text-base transition-opacity duration-300',
        isTransitioning ? 'opacity-0' : 'opacity-100',
        className
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      {messages[currentIndex]}
    </p>
  );
}
